/**
 * useTripRealtime - WebSocket connection to /trip/trips/{tripId}/ws.
 * Single connection per hook instance; exponential backoff reconnect
 * (1s → 30s cap); dispatches a local `RECONNECTED` pseudo-event after a
 * successful reconnect so subscribers can trigger a full snapshot refetch.
 * See contracts/realtime-events.md.
 */

import { useCallback, useEffect, useRef } from 'react';
import { REALTIME_EVENTS, type RealtimeEventName, type RealtimeEventPayloadMap } from '../utils/realtimeEvents';

const MAX_BACKOFF_MS = 30000;

type Listener = (data: unknown) => void;

export function useTripRealtime(tripId: string | undefined) {
  const listenersRef = useRef<Map<string, Set<Listener>>>(new Map());
  const wsRef = useRef<WebSocket | null>(null);
  const retryCountRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const unmountedRef = useRef(false);

  const dispatch = useCallback((event: string, data: unknown) => {
    listenersRef.current.get(event)?.forEach((cb) => cb(data));
  }, []);

  useEffect(() => {
    unmountedRef.current = false;
    if (!tripId) return;

    const connect = () => {
      if (unmountedRef.current) return;

      const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
      const ws = new WebSocket(`${protocol}://${window.location.host}/trip/trips/${tripId}/ws`);
      wsRef.current = ws;

      ws.onopen = () => {
        const wasReconnect = retryCountRef.current > 0;
        retryCountRef.current = 0;
        if (wasReconnect) dispatch(REALTIME_EVENTS.RECONNECTED, {});
      };

      ws.onmessage = (evt) => {
        try {
          const message = JSON.parse(evt.data);
          if (message?.event) dispatch(message.event, message.data);
        } catch {
          // 忽略無法解析的訊息（後端契約保證是 JSON，異常情況下不影響其他事件處理）
        }
      };

      ws.onclose = () => {
        if (unmountedRef.current) return;
        const delay = Math.min(MAX_BACKOFF_MS, 1000 * 2 ** retryCountRef.current);
        retryCountRef.current += 1;
        reconnectTimerRef.current = setTimeout(connect, delay);
      };

      ws.onerror = () => {
        ws.close();
      };
    };

    connect();

    return () => {
      unmountedRef.current = true;
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      wsRef.current?.close();
      wsRef.current = null;
      retryCountRef.current = 0;
    };
  }, [tripId, dispatch]);

  const subscribe = useCallback(
    <E extends RealtimeEventName>(event: E, callback: (data: RealtimeEventPayloadMap[E]) => void) => {
      if (!listenersRef.current.has(event)) listenersRef.current.set(event, new Set());
      const set = listenersRef.current.get(event)!;
      const listener = callback as Listener;
      set.add(listener);
      return () => {
        set.delete(listener);
      };
    },
    []
  );

  return { subscribe };
}
